import {
    Box,
    Button,
    Hidden,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Theme,
    Typography,
    useTheme,
} from "@mui/material";
import TableHeadComponent from "./TableHeadComponent";
import TableBodyComponent from "./TableBodyComponent";
import { CustomTableProps } from "../../types";
import TablePaginationComponent from "./TablePaginationComponent";
import { DropDownIcons } from "../../../../assets/SvgIcons";
import { useState } from "react";
import { BottomMenu } from "../BottomMenu";


export default function CustomTable(
    {
        isLoading = false,
        tableBody = <></>,
        tableHeaderData = [],
        tableContainerStyles = {},
        tableHeadRowCustomStyles = {},
        tableBodyCustomStyles = {},
        tableBodyLoaderRowCustomStyles = {},
        isPaginationVisible = true,
        tableDataCount = 0,
        handlePageChange = () => { return; },
        handleRowsPerPageChange = () => { return; },
        handleSort = () => { return; },
        isShowMoreVisible = false,
        handleShowMore = () => { return; },
        loadingRowsNum = 10,
        noResultMsg = "No Data Found",
        rowsPerPageOptions = [10, 20, 30],
        rowsPerPage = 10,
        currentPage = 0,
        order = "asc",
        orderBy = "",
        tableData = [],
        manualResponsiveBody = false,
        ManualResponsiveBodyComponent,
        isScrollTable = false,
        paginationStyles = {},
    }: CustomTableProps): JSX.Element {
    const { palette } = useTheme()

    const [tableHeaderMenu, setTableHeaderMenu] = useState<boolean>(false)
    const [tableHeaderKey, setTableHeaderKey] = useState<string>(tableHeaderData?.[1]?.key || tableHeaderData?.[1]?.id)

    const style: any = { color: palette.text.secondary, fontWeight: 500, fontSize: '14px', textTransform: 'uppercase', textWrap: 'nowrap', paddingRight: 0, paddingLeft: 0 }

    const handleHeader = () => {
        setTableHeaderMenu(true)
    }


    return (
        <>
            <Hidden only={'xs'}>
                <TableContainer sx={tableContainerStyles}>
                    <Table>
                        <TableHeadComponent
                            isLoading={isLoading}
                            tableHeadRowCustomStyles={tableHeadRowCustomStyles}
                            tableHeaderData={tableHeaderData}
                            handleSort={handleSort}
                            tableDataCount={tableDataCount}
                            order={order}
                            orderBy={orderBy}
                        />
                        <TableBodyComponent
                            isLoading={isLoading}
                            tableDataCount={tableDataCount}
                            tableBodyCustomStyles={tableBodyCustomStyles}
                            loadingRowsNum={loadingRowsNum}
                            loadingColsNum={tableHeaderData?.length ?? 0}
                            tableBodyLoaderRowCustomStyles={tableBodyLoaderRowCustomStyles}
                            tableBody={tableBody}
                        />
                    </Table>
                    <Box
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {!isLoading && tableDataCount === 0 && (
                            <Typography
                                key={noResultMsg}
                                variant="body_600"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary
                                })}
                            >
                                {noResultMsg}
                            </Typography>
                        )}
                    </Box>
                </TableContainer>
            </Hidden>
            <Hidden smUp={true}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableCell align='left' style={{ ...style }}>
                                {tableHeaderData?.[0]?.label}
                            </TableCell>
                            <TableCell align='center' sx={{
                                ...style,
                            }}>
                                <TableSortLabel
                                    onClick={() => handleHeader()}
                                    sx={{
                                        // textOverflow: 'ellipsis',
                                        // whiteSpace: 'nowrap',
                                        // overflow: 'hidden',
                                        // maxWidth: '50px'
                                    }}
                                    IconComponent={() => <DropDownIcons style={{ marginRight: '5px' }} />} >
                                    {tableHeaderData?.filter((item) => (item?.id || item?.key) === tableHeaderKey)[0]?.label}
                                </TableSortLabel>
                            </TableCell>
                        </TableHead>
                        {!manualResponsiveBody &&
                            <TableBody>
                                {tableData?.map((item: any, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell
                                            style={{ ...style }}
                                            align='left'
                                        >
                                            <Typography
                                                variant="subtitle_500"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.primary[500],
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    maxWidth: '150px'
                                                })}
                                            >
                                                {item?.[tableHeaderData[0]?.id]}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            style={{ ...style }}
                                            align='center'
                                        >
                                            <Typography
                                                variant="subtitle_500"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.text.primary,
                                                })}
                                            >
                                                {tableHeaderKey?.includes('.') ? item?.[tableHeaderKey.split('.')[0]][tableHeaderKey.split('.')[1]] : item?.[tableHeaderKey]}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        }
                        {manualResponsiveBody &&
                            ManualResponsiveBodyComponent}
                    </Table>
                </TableContainer>

            </Hidden>
            {isPaginationVisible && !isLoading && tableDataCount > 0 && (
                <Box
                    sx={{
                        marginTop: "10px",
                        ...(isScrollTable && paginationStyles)
                    }}
                >
                    <TablePaginationComponent
                        tableDataCount={tableDataCount}
                        handlePageChange={handlePageChange}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                        currentPage={currentPage}
                    />
                </Box>
            )}
            {isShowMoreVisible && !isLoading && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "20px"
                    }}
                >
                    <Button
                        variant="text"
                        sx={(theme: Theme) => ({
                            color: theme.palette.primary[500],
                            padding: "8px",
                            ...theme.typography.body_500
                        })}
                        onClick={handleShowMore}
                    >
                        Show More
                    </Button>
                </Box>
            )}

            <BottomMenu open={tableHeaderMenu} close={() => setTableHeaderMenu(false)} list={tableHeaderData} setTableHeaderKey={setTableHeaderKey} />
        </>
    )
}